import React, { useState, useEffect } from "react";
import UserModal from "../../Components/UserModal";
import axios from "axios";
import TransactionHistory from "../../Components/TransactionHistory";
import { FaTrashAlt, FaCheck } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import PromoteConfirmModal from "../../Components/PromoteConfirmModal";
import DeleteConfirmationModal from "../../Components/DeleteConfirmationModal";
import { toast } from "react-toastify";
import SpecialAgentModal from "../../Components/SpecialAgentModal";

const BASE_URL = process.env.REACT_APP_API_URL;

export default function AgentsPage() {
  const [activeTab, setActiveTab] = useState("agents");
  const [userRequests, setUserRequests] = useState([]);
  const [agents, setAgents] = useState([]);
  const [subagents, setSubagents] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isCreatingSubagent, setIsCreatingSubagent] = useState(false);
  const [history, setHistory] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const [currentUserRole, setCurrentUserRole] = useState();
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentTransactionPage, setCurrentTransactionPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToPromote, setUserToPromote] = useState(null);
  const [updatedUsers, setUpdatedUsers] = useState({});
  const [promoteLoading, setPromoteLoading] = useState(false);

  const [showSpecialAgentModal, setShowSpecialAgentModal] = useState(false);
  const [selectedSpecialAgent, setSelectedSpecialAgent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isCurrentUserSpecialAgent, setIsCurrentUserSpecialAgent] =
    useState(false);

  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const checkSpecialAgentStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        const user = JSON.parse(localStorage.getItem("user"));

        if (user?._id) {
          const response = await axios.get(
            `${BASE_URL}/special-agent/${user._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          const isSpecialAgent = Boolean(
            response.data?.agentData?.isSpecialAgent ||
            (Array.isArray(response.data?.agentData) &&
              response.data.agentData.length > 0),
          );

          setIsCurrentUserSpecialAgent(isSpecialAgent);
        }
      } catch (error) {
        console.error("Error checking special agent status:", error);
        setIsCurrentUserSpecialAgent(false);
      }
    };

    checkSpecialAgentStatus();
  }, []);

  const handleSpecialAgentUpgrade = (user) => {
    setSelectedSpecialAgent(user);
    setShowSpecialAgentModal(true);
  };

  const handleConfirmSpecialAgent = async (formData) => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${BASE_URL}/special-agent/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent._id === selectedSpecialAgent._id
            ? { ...agent, isSpecialAgent: true }
            : agent,
        ),
      );

      toast.success("Agent promoted to special agent successfully");
      setShowSpecialAgentModal(false);

      await fetchAllAgents();
    } catch (error) {
      console.error("Promotion failed:", error);
      toast.error(error.response?.data?.error || "Failed to promote agent");
    }
  };

  const handlePromoteClick = (user) => {
    setUserToPromote(user);
    setIsModalOpen(true);
  };

  const handleConfirmPromote = async (user) => {
    setPromoteLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(
        `${BASE_URL}/users/user/${user._id}/promote`,
        { email: user.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      setUserRequests((prev) => prev.filter((u) => u._id !== user._id));
      setAgents((prev) => [...prev, response.data.user]);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Promotion failed:", error);
      alert("Failed to promote user.");
    } finally {
      setPromoteLoading(false);
    }
  };

  const userRole = JSON.parse(
    localStorage.getItem("user"),
  )?.role?.toLowerCase();

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (userId) => {
    try {
      await axios.delete(`${BASE_URL}/users/delete-agents/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      toast.success("User deleted successfully");
      setAgents((prev) => prev.filter((user) => user._id !== userId));
      setShowDeleteModal(false);
    } catch (error) {
      toast.error("Delete failed");
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedAgentId]);

  useEffect(() => {
    setCurrentTransactionPage(1);
  }, [selectedUserId]);

  const getCurrentItems = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const getTotalPages = (items) => {
    return Math.ceil(items.length / itemsPerPage);
  };

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const parsed = JSON.parse(userData);
      const role = parsed.role?.toLowerCase() || "admin";
      setCurrentUserRole(role);
    }
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const fetchAllUserRequests = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-user-requests`,
        getAuthHeaders(),
      );
      setUserRequests(response.data || []);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch user-requests.");
    }
  };

  const fetchAllAgents = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-agents`,
        getAuthHeaders(),
      );
      setAgents(response.data || []);
      if (currentUserRole === "agent") {
        const currentUserId = localStorage.getItem("userId");
        setSelectedAgentId(currentUserId);
      }
    } catch (error) {
      console.error(error);
      alert("Failed to fetch agents.");
    }
  };

  const fetchSubagentsForAgent = async (agentId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-sub-agents`,
        getAuthHeaders(),
      );
      const filteredSubagents = (response.data || []).filter(
        (subagent) => String(subagent.parentId) === String(agentId),
      );
      setSubagents(filteredSubagents);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch subagents.");
    }
  };

  const fetchAllSubagents = async () => {
    try {
      const response = await axios.get(
        `${BASE_URL}/users/get-all-sub-agents`,
        getAuthHeaders(),
      );
      setSubagents(response.data.users || []);
    } catch (error) {
      console.error(error);
      alert("Failed to fetch subagents.");
    }
  };

  useEffect(() => {
    if (currentUserRole === "admin") {
      fetchAllAgents();
      fetchAllUserRequests();
      if (activeTab === "subagents") {
        fetchAllSubagents();
      }
    } else if (currentUserRole === "agent") {
      setActiveTab("subagents");
      const userData = localStorage.getItem("user");
      let currentUserId = null;
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          currentUserId = parsed._id;
        } catch {}
      }
      if (currentUserId) {
        fetchSubagentsForAgent(currentUserId);
      } else {
        setSubagents([]);
      }
    }
  }, [activeTab, currentUserRole]);

  let selectedUser = null;
  if (selectedUserId) {
    if (activeTab === "agents") {
      selectedUser = agents.find((u) => u?._id === selectedUserId) || null;
    } else if (activeTab === "subagents") {
      selectedUser = subagents.find((u) => u?._id === selectedUserId) || null;
    } else if (activeTab === "userRequests") {
      selectedUser =
        userRequests.find((u) => u?._id === selectedUserId) || null;
    }
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      let response;

      if (modalData._id) {
        // ── EDIT MODE ──────────────────────────────────────────────────────
        if (activeTab === "userRequests") {
          response = await axios.put(
            `${BASE_URL}/users/user/${modalData._id}`,
            {
              name: modalData.name,
              phone: modalData.phone,
              gender: modalData.gender,
              age: modalData.age,
              status: modalData.status,
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } else {
          const endpoint =
            activeTab === "agents"
              ? `${BASE_URL}/users/update-agent/${modalData.entityId}`
              : `${BASE_URL}/users/update-sub-agent/${modalData.entityId}`;

          response = await axios.put(
            endpoint,
            {
              name: modalData.name,
              phone: modalData.phone,
              gender: modalData.gender,
              age: modalData.age,
              status: modalData.status,
              servicePrices: modalData.servicePrices || {},
              serviceAccess: modalData.serviceAccess || [],
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }

        if (activeTab === "agents") {
          await fetchAllAgents();
        } else if (activeTab === "subagents") {
          if (currentUserRole === "admin") {
            await fetchAllSubagents();
          } else {
            const userData = localStorage.getItem("user");
            let currentUserId = null;
            try {
              const parsed = JSON.parse(userData);
              currentUserId = parsed._id;
            } catch {}
            if (currentUserId) await fetchSubagentsForAgent(currentUserId);
          }
        } else if (activeTab === "userRequests") {
          await fetchAllUserRequests();
        }

        setSelectedUserId(modalData._id);
        toast.success("User updated successfully");
      } else {
        // ── CREATE MODE ────────────────────────────────────────────────────
        if (isCreatingSubagent) {
          response = await axios.post(
            `${BASE_URL}/users/register-sub-agent`,
            {
              ...modalData,
              servicePrices: modalData.servicePrices || {},
              serviceAccess: modalData.serviceAccess || [],
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );

          // ── KEY CHANGE: differentiate admin (201) vs agent (202) response ──
          if (response.status === 202) {
            // Agent created sub-agent — pending admin approval
            toast.info(
              "Request sent to admin. Kindly wait for the request to be approved.",
              { autoClose: 5000 },
            );
          } else {
            // Admin created sub-agent directly — user object is returned
            setSubagents((prev) => [...prev, response.data.user]);
            toast.success("Sub-agent created successfully");
          }
        } else {
          response = await axios.post(
            `${BASE_URL}/users/register-agent`,
            {
              ...modalData,
              servicePrices: modalData.servicePrices || {},
              serviceAccess: modalData.serviceAccess || [],
            },
            { headers: { Authorization: `Bearer ${token}` } },
          );
          setAgents((prev) => [...prev, response.data.user]);
          toast.success("Agent created successfully");
        }
      }

      setShowModal(false);
      setModalData(null);
    } catch (error) {
      console.error(error);
      toast.error(
        error.response?.data?.message || "Error occurred during save",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    if (!selectedUser) {
      alert("Please select a user to edit.");
      return;
    }
    setModalData({
      ...selectedUser,
      entityId: selectedUser.entityId,
      servicePrices: selectedUser.servicePrices || {},
      serviceAccess: selectedUser.serviceAccess || [],
    });
    setShowModal(true);
  };

  const handlePromote = async () => {
    if (!selectedUser) {
      alert("Please select a user to promote.");
      return;
    }
    const confirmed = window.confirm(
      `Promote ${selectedUser.name} to Subagent?`,
    );
    if (!confirmed) return;
    try {
      const response = await axios.put(
        `${BASE_URL}/users/user/${selectedUser._id}/promote`,
        {},
        getAuthHeaders(),
      );
      alert(response.data.message || "User promoted successfully");
      await fetchAllUserRequests();
      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to promote user.");
    }
  };

  const handleDeactivate = async () => {
    if (!selectedUser) {
      alert("Please select a user to deactivate.");
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUser.name} to inactive?`);
    if (!confirmed) return;

    try {
      const isAgent = selectedUser.role === "agent";
      const endpoint = isAgent
        ? `${BASE_URL}/users/update-agent/${selectedUser.entityId}`
        : `${BASE_URL}/users/update-sub-agent/${selectedUser.entityId}`;

      await axios.put(endpoint, { status: "inactive" }, getAuthHeaders());

      alert("User deactivated successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
      } else {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to deactivate user.");
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedUser) {
      alert("Please select a user to deactivate.");
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUser.name} to inactive?`);
    if (!confirmed) return;

    try {
      const endpoint = `${BASE_URL}/users/${selectedUser._id}/deactivate`;
      await axios.put(endpoint, { status: "inactive" }, getAuthHeaders());

      alert("User deactivated successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
        if (currentUserRole === "admin") {
          await fetchAllUserRequests();
        }
      } else if (activeTab === "subagents") {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
          await fetchAllUserRequests();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      } else if (activeTab === "userRequests") {
        await fetchAllUserRequests();
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to deactivate user.");
    }
  };

  const handleRestore = async () => {
    if (!selectedUser) {
      alert("Please select a user to restore.");
      return;
    }

    const confirmed = window.confirm(`Reactivate ${selectedUser.name}?`);
    if (!confirmed) return;

    try {
      const isAgent = selectedUser.role === "agent";
      const endpoint = isAgent
        ? `${BASE_URL}/users/update-agent/${selectedUser.entityId}`
        : `${BASE_URL}/users/update-sub-agent/${selectedUser.entityId}`;

      await axios.put(endpoint, { status: "active" }, getAuthHeaders());

      alert("User restored successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
      } else {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to restore user.");
    }
  };

  const handleCreateAgent = () => {
    setModalData({
      name: "",
      email: "",
      phone: "",
      password: "",
      gender: "",
      age: "",
      role: "agent",
      status: "active",
    });
    setIsCreatingSubagent(false);
    setShowModal(true);
  };

  const handleCreateSubagent = () => {
    if (currentUserRole === "admin" && !selectedAgentId) {
      alert("Please select an agent first to create a subagent under them.");
      return;
    }

    const agentId =
      currentUserRole === "agent"
        ? localStorage.getItem("userId")
        : selectedAgentId;

    setModalData({
      name: "",
      email: "",
      phone: "",
      password: "",
      gender: "",
      age: "",
      role: "sub-agent",
      status: "active",
      parentId: agentId,
    });
    setIsCreatingSubagent(true);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) {
      alert("Please select a user to delete.");
      return;
    }

    const confirmed = window.confirm(`Set ${selectedUser.name} to inactive?`);
    if (!confirmed) return;

    try {
      const isAgent = selectedUser.role === "agent";
      const endpoint = isAgent
        ? `${BASE_URL}/users/update-agent/${selectedUser.entityId}`
        : `${BASE_URL}/users/update-sub-agent/${selectedUser.entityId}`;

      await axios.put(endpoint, { status: "inactive" }, getAuthHeaders());

      alert("User status updated to inactive successfully");

      if (activeTab === "agents") {
        await fetchAllAgents();
      } else {
        if (currentUserRole === "admin") {
          await fetchAllSubagents();
        } else {
          await fetchSubagentsForAgent(localStorage.getItem("userId"));
        }
      }

      setSelectedUserId(null);
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || "Failed to update user status.");
    }
  };

  const handleInputChange = (e) => {
    setModalData({ ...modalData, [e.target.name]: e.target.value });
  };

  const handleAgentSelect = (e) => {
    const agentId = e.target.value;
    setSelectedAgentId(agentId);
    if (agentId) {
      fetchSubagentsForAgent(agentId);
    } else {
      setSubagents([]);
    }
  };

  const fetchAgentTransactions = async (userId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/services/get-agent-transactions?userId=${userId}`,
        getAuthHeaders(),
      );
      setTransactions(response.data.data || []);
    } catch (error) {
      console.error("Error fetching transactions:", error);
      alert("Failed to fetch transactions");
    }
  };

  useEffect(() => {
    if (selectedUserId) {
      fetchAgentTransactions(selectedUserId);
    } else {
      setTransactions([]);
    }
  }, [selectedUserId]);

  const activeList =
    activeTab === "agents"
      ? agents
      : activeTab === "subagents"
        ? subagents
        : userRequests;

  const filteredData = activeList.filter(
    (user) =>
      user?.name?.toLowerCase().includes(searchQuery) ||
      user?.phone?.toLowerCase().includes(searchQuery),
  );

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <h2 className="text-3xl text-center font-bold mb-4">User Management</h2>

        {/* Tabs */}
        {currentUserRole === "admin" && (
          <div className="flex border-b mb-4">
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === "agents"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("agents")}
            >
              Agents
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === "subagents"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("subagents")}
            >
              Subagents
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === "userRequests"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500"
              }`}
              onClick={() => setActiveTab("userRequests")}
            >
              User Requests
            </button>
          </div>
        )}

        {/* Agent selector for admin in subagents tab */}
        {currentUserRole === "admin" && activeTab === "subagents" && (
          <div className="mb-4">
            <label
              htmlFor="agentSelect"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Select Agent
            </label>
            <select
              id="agentSelect"
              value={selectedAgentId || ""}
              onChange={handleAgentSelect}
              className="w-full p-2 border rounded-md"
            >
              <option value="">-- Select an Agent --</option>
              {agents.map((agent) => (
                <option key={agent._id} value={agent._id}>
                  {agent.name} ({agent.email})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-end mb-4 gap-2">
          <div className="flex justify-end mb-4 gap-2">
            {activeTab === "agents" && currentUserRole === "admin" && (
              <button
                onClick={handleCreateAgent}
                className="px-3 py-2 bg-green-600 text-white rounded-md text-sm"
              >
                Create Agent
              </button>
            )}
            {activeTab === "subagents" && (
              <button
                onClick={handleCreateSubagent}
                className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm"
              >
                Create Subagent
              </button>
            )}
            {(currentUserRole === "admin" || isCurrentUserSpecialAgent) && (
              <button
                onClick={handleEdit}
                className="px-3 py-2 bg-yellow-500 text-white rounded-md text-sm"
                disabled={!selectedUser}
              >
                Edit
              </button>
            )}
            {selectedUser?.status === "active" && (
              <button
                onClick={handleDeactivate}
                className="px-3 py-2 bg-red-500 text-white rounded-md text-sm"
              >
                Deactivate
              </button>
            )}
            {selectedUser?.status === "inactive" && (
              <button
                onClick={handleRestore}
                className="px-3 py-2 bg-green-500 text-white rounded-md text-sm"
              >
                Restore
              </button>
            )}
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow overflow-x-auto">
          {/* Search bar */}
          <div className="flex justify-between items-center mb-4">
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
              className="border p-2 rounded-md w-64 focus:ring-2 focus:ring-purple-500 outline-none"
            />
          </div>
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-100 font-semibold">
              <tr>
                <th className="p-2">Name</th>
                <th className="p-2">Email</th>
                <th className="p-2">Phone</th>
                <th className="p-2">Gender</th>
                <th className="p-2">Age</th>
                <th className="p-2">Role</th>
                {currentUserRole === "admin" && (
                  <th className="p-2 w-36">Special Agent Status</th>
                )}
                <th className="p-2">Status</th>
                {currentUserRole === "admin" && (
                  <th className="p-2">Promote</th>
                )}
                <th className="p-2">Actions</th>
              </tr>
              {(!agents.length && activeTab === "agents") ||
              (!subagents.length && activeTab === "subagents") ||
              (!userRequests.length && activeTab === "userRequests") ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-gray-400">
                    No {activeTab} found.
                  </td>
                </tr>
              ) : null}
            </thead>

            <tbody>
              {getCurrentItems(filteredData).map((user) => (
                <tr
                  key={user?._id || Math.random()}
                  onClick={() => user?._id && setSelectedUserId(user._id)}
                  className={`cursor-pointer border-b ${
                    selectedUserId === user?._id
                      ? user?.status === "inactive"
                        ? "bg-purple-200"
                        : "bg-purple-100"
                      : user?.status === "inactive"
                        ? "bg-gray-100 text-gray-500"
                        : "hover:bg-gray-100"
                  }`}
                >
                  <td className="p-2">{user?.name || "-"}</td>
                  <td className="p-2">{user?.email || "-"}</td>
                  <td className="p-2">{user?.phone || "-"}</td>
                  <td className="p-2">{user?.gender || "-"}</td>
                  <td className="p-2">{user?.age || "-"}</td>
                  <td className="p-2">{user?.role || "-"}</td>

                  {currentUserRole === "admin" && (
                    <td className="p-2">
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          !user?.isSpecialAgent &&
                            handleSpecialAgentUpgrade(user);
                        }}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user?.isSpecialAgent
                            ? "bg-green-100 text-green-800 cursor-default"
                            : "bg-gray-100 text-gray-800 cursor-pointer hover:bg-gray-200"
                        }`}
                      >
                        {user?.isSpecialAgent ? "Yes" : "Promote"}
                      </span>
                    </td>
                  )}

                  <td className="p-2">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user?.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {user?.status || "-"}
                    </span>
                  </td>

                  {currentUserRole === "admin" && (
                    <td className="p-2">
                      {user?.role === "agent" ? (
                        <FaCheck color="#4CAF50" size={20} />
                      ) : (
                        <FaTimes
                          color="#F44336"
                          size={20}
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePromoteClick(user);
                          }}
                        />
                      )}
                    </td>
                  )}

                  <td className="p-2 flex items-center space-x-3">
                    {(currentUserRole === "admin" ||
                      isCurrentUserSpecialAgent) && (
                      <FaTrashAlt
                        color="#F44336"
                        size={18}
                        className="cursor-pointer"
                        title="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(user);
                        }}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination controls */}
          {filteredData.length > itemsPerPage && (
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-gray-700">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
                {filteredData.length} entries
              </span>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
                >
                  Prev
                </button>

                {Array.from(
                  { length: getTotalPages(filteredData) },
                  (_, i) => i + 1,
                )
                  .slice(
                    Math.max(currentPage - 3, 0),
                    Math.min(currentPage + 3, getTotalPages(filteredData)),
                  )
                  .map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 border rounded ${
                        currentPage === page
                          ? "bg-purple-600 text-white"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  ))}

                <button
                  onClick={() => setCurrentPage(getTotalPages(filteredData))}
                  disabled={currentPage === getTotalPages(filteredData)}
                  className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
                >
                  Last
                </button>

                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(prev + 1, getTotalPages(filteredData)),
                    )
                  }
                  disabled={currentPage === getTotalPages(filteredData)}
                  className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedUser && <TransactionHistory userId={selectedUser._id} />}
        <UserModal
          show={showModal}
          onClose={() => setShowModal(false)}
          onSubmit={handleFormSubmit}
          data={modalData}
          onChange={handleInputChange}
          isAdmin={currentUserRole === "admin"}
          isSpecialAgent={isCurrentUserSpecialAgent}
          currentUser={JSON.parse(localStorage.getItem("user"))}
          isSubmitting={isSubmitting}
        />

        <PromoteConfirmModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={handleConfirmPromote}
          user={userToPromote}
          isLoading={promoteLoading}
        />

        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          user={userToDelete}
        />

        <SpecialAgentModal
          show={showSpecialAgentModal}
          onClose={() => setShowSpecialAgentModal(false)}
          onSubmit={handleConfirmSpecialAgent}
          agent={selectedSpecialAgent}
        />
      </div>
    </>
  );
}
